import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Table, Spin, Tag, Alert, Switch } from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { API_URL } from "../../constants";

// Fallback API_URL
// const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/";

const Calculari = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculationLoading, setIsCalculationLoading] = useState(false);
  const [blocked, setBlocked] = useState(true);
  const [logContent, setLogContent] = useState("");
  const [logFileName, setLogFileName] = useState(null);
  const [error, setError] = useState(null);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const token = useSelector((state) => state.user.currentUser?.accessToken);
  const pollingIntervalRef = useRef(null);

  // Validate API_URL
  if (!API_URL) {
    console.error("API_URL is not defined.");
    setError("Configuration error: API_URL is not defined.");
  }

  const getBlockStatus = async () => {
    try {
      const res = await axios.get(API_URL + "/gestionar/blockageStatus/month");
      console.log({ res });
      const data = res.data;
      setBlocked(JSON.parse(data.blocked));
    } catch (error) {
      console.log({ error });
    }
  };

  // Fetch log content
  const fetchLogContent = async (fileName) => {
    try {
      setError(null);
      const response = await axios.get(
        `${API_URL}user/calculari/logs/${fileName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLogContent(response.data);
      setLogFileName(fileName);
    } catch (err) {
      console.error("Failed to fetch log content:", err);
      setError("Failed to fetch log content.");
    }
  };

  // Fetch list of logs
  const fetchLogsList = async (filterPrefix = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}user/calculari/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const formattedLogs = response.data.map((fileName) => {
        // Parse filename: log_YYYY-MM-DD_HH-MM-SS.txt or reset_log_YYYY-MM-DD_HH-MM-SS.txt
        const timestampStr = fileName.startsWith("reset_log_")
          ? fileName.replace("reset_log_", "")
          : fileName.replace("log_", "");
        const createdDate = dayjs(
          timestampStr.replace(".txt", ""),
          "YYYY-MM-DD_HH-mm-ss"
        ).toDate();
        return {
          id: fileName,
          filename: fileName,
          createdDate,
        };
      });
      // Sort logs with most recent first
      const sortedLogs = formattedLogs.sort((a, b) =>
        dayjs(b.createdDate).diff(dayjs(a.createdDate))
      );
      // Filter by prefix if specified (e.g., "reset_log_" for reset)
      const filteredLogs = filterPrefix
        ? sortedLogs.filter((log) => log.filename.startsWith(filterPrefix))
        : sortedLogs;
      setLogs(sortedLogs); // Store all logs for table display
      return filteredLogs.length > 0 ? filteredLogs : sortedLogs; // Return filtered or all logs
    } catch (error) {
      console.error("Failed to fetch logs list:", error);
      setError("Failed to fetch logs list.");
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Get the latest log and display it
  const fetchAndDisplayLatestLog = (filterPrefix = null) => {
    fetchLogsList(filterPrefix).then((updatedLogs) => {
      if (updatedLogs.length > 0) {
        const latestLog = updatedLogs[0]; // First item is the most recent
        fetchLogContent(latestLog.filename);
        if (isAutoRefreshEnabled) {
          pollLogs(latestLog.filename); // Start polling if auto-refresh is enabled
        }
      }
    });
  };

  // Polling for log updates
  const pollLogs = (fileName) => {
    if (!fileName || !isAutoRefreshEnabled) return; // Exit if no fileName or auto-refresh is disabled
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API_URL}user/calculari/logs/${fileName}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLogContent(response.data);
        setLogFileName(fileName); // Ensure logFileName is set during polling
        if (response.data.includes("[OK] Calcul")) {
          clearInterval(pollingIntervalRef.current);
          setIsCalculationLoading(false);
          setLogContent((prev) => prev + "\n[INFO] Calculation completed.");
          fetchLogsList(); // Refresh logs list
        } else if (response.data.includes("[API] Calculation failed:")) {
          clearInterval(pollingIntervalRef.current);
          setIsCalculationLoading(false);
          setLogContent((prev) => prev + "\n[ERROR] Calculation failed.");
          fetchLogsList(); // Refresh logs list
        }
      } catch (err) {
        console.error("Polling failed:", err);
        clearInterval(pollingIntervalRef.current);
        setIsCalculationLoading(false);
        setError("Polling failed. Check API connection.");
      }
    }, 2000); // Poll every 2 seconds
  };

  // Start calculation and fetch latest log
  const startCalculation = async () => {
    setLogContent("");
    setLogFileName(null);
    setError(null);
    setIsCalculationLoading(true);

    // Fetch and display the latest log in the background
    fetchAndDisplayLatestLog("log_"); // Prefer regular log files

    // Run API call in the background
    try {
      const response = await axios.post(
        `${API_URL}user/calculari/start-calculation`,
        { dateString: dayjs().subtract(1, "month").format("YYYY-MM-DD") },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Use logFileName from response if available
      const { logFileName: responseLogFileName } = response.data;
      if (responseLogFileName && isAutoRefreshEnabled) {
        pollLogs(responseLogFileName);
        setLogFileName(responseLogFileName);
        fetchLogContent(responseLogFileName); // Update content with response log
      }
      setIsCalculationLoading(false);
      fetchLogsList(); // Refresh logs list
    } catch (err) {
      console.error("Failed to start calculation:", err);
      setIsCalculationLoading(false);
      setError("Failed to start calculation.");
    }
  };

  // Reset calculation and fetch latest reset log
  const resetCalculation = async () => {
    setLogContent("");
    setLogFileName(null);
    setError(null);
    setIsCalculationLoading(true);

    // Fetch and display the latest reset log in the background
    fetchAndDisplayLatestLog("reset_log_"); // Prefer reset log files

    // Run API call in the background
    try {
      const response = await axios.post(
        `${API_URL}user/calculari/reset`,
        { dateString: dayjs().subtract(1, "month").format("YYYY-MM-DD") },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Use logFileName from response if available
      const { logFileName: responseLogFileName } = response.data;
      if (responseLogFileName && isAutoRefreshEnabled) {
        pollLogs(responseLogFileName);
        setLogFileName(responseLogFileName);
        fetchLogContent(responseLogFileName); // Update content with response log
      }
      setIsCalculationLoading(false);
      fetchLogsList(); // Refresh logs list
    } catch (err) {
      console.error("Failed to reset calculation:", err);
      setIsCalculationLoading(false);
      setError("Failed to reset calculation.");
    }
  };

  // Toggle auto-refresh
  const toggleAutoRefresh = (checked) => {
    setIsAutoRefreshEnabled(checked);
    if (!checked && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current); // Stop polling if disabled
    } else if (checked && logFileName) {
      pollLogs(logFileName); // Resume polling if enabled and a log file is selected
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    getBlockStatus();
    fetchLogsList();
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const mainColumns = [
    {
      title: "Created At",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date) => dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "Filename",
      dataIndex: "filename",
      key: "filename",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => fetchLogContent(record.filename)}
          icon={<DownloadOutlined />}
          aria-label={`View log for ${record.filename}`}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="d-flex gap-4 flex-column w-100">
      <div className="d-flex gap-2 align-items-center">
        <Button
          type="primary"
          onClick={startCalculation}
          disabled={isCalculationLoading || blocked}
          aria-busy={isCalculationLoading || blocked}
          aria-label="Start calculation"
        >
          {isCalculationLoading ? <Spin size="small" /> : "Start Calculation"}
        </Button>
        {/*<Button*/}
        {/*  type="default"*/}
        {/*  onClick={resetCalculation}*/}
        {/*  disabled={isCalculationLoading}*/}
        {/*  aria-busy={isCalculationLoading}*/}
        {/*  aria-label="Reset calculation"*/}
        {/*>*/}
        {/*  Reset Calculation*/}
        {/*</Button>*/}
        <Button
          type="default"
          onClick={() => fetchLogsList()}
          disabled={isLoading}
          icon={<ReloadOutlined />}
          aria-label="Refresh logs list"
        >
          Refresh Logs
        </Button>
        <Switch
          checked={isAutoRefreshEnabled}
          onChange={toggleAutoRefresh}
          checkedChildren="Auto-refresh On"
          unCheckedChildren="Auto-refresh Off"
          aria-label="Toggle auto-refresh"
        />
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      <Card
        title={
          <div className="d-flex justify-content-between align-items-center">
            <span>
              {logFileName ? (
                <>
                  Logs - <Tag color="blue">{logFileName}</Tag>
                </>
              ) : (
                "Logs (waiting for calculation)"
              )}
            </span>
            {isCalculationLoading && (
              <Tag color="green" icon={<ReloadOutlined spin />}>
                Calculating
              </Tag>
            )}
          </div>
        }
        bordered
        className="w-100"
        style={{ height: "400px" }}
        bodyStyle={{ height: "100%", overflowY: "auto", padding: "10px" }}
      >
        <div
          style={{
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
            fontSize: "12px",
            color: "#333",
          }}
          role="log"
          aria-live="polite"
        >
          {logContent || "No logs to display."}
        </div>
      </Card>

      <Card title="Log History" loading={isLoading} bordered className="w-100">
        <Table
          columns={mainColumns}
          dataSource={logs}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default Calculari;
